import {
	type MaterialProperty,
	type MaterialPropertyListElement,
	MaterialPropertyView
} from '$lib/mmpv/types/materialProperty'
import { translate } from '$i18n/i18n'

abstract class ViewHandler {
	view: MaterialPropertyView
	title: string = ''
	breadcrumbTitle: string = ''
	description: string = ''
	materialPropertyTooltip: string = ''

	constructor(view: MaterialPropertyView) {
		this.view = view
	}

	abstract getViewVariables(): {
		title: string
		breadcrumbTitle: string
		description: string
		materialPropertyTooltip?: string
	}

	abstract setViewVariables(): void
}

export class MaterialPropertyHandler extends ViewHandler {
	materialProperty: MaterialProperty
	isNew: boolean
	readonly #propertyParents: Map<string, string[]>
	readonly #properties: Map<string, MaterialProperty>

	constructor(
		materialProperty: MaterialProperty,
		isNew: boolean,
		propertyParents: Map<string, string[]>,
		properties: Map<string, MaterialProperty>
	) {
		super(MaterialPropertyView.MATERIAL_PROPERTY)
		this.materialProperty = materialProperty
		this.isNew = isNew
		this.#properties = properties
		this.#propertyParents = propertyParents
		this.buildMaterialPropertyParentsInfoTooltip()
		this.setViewVariables()
		return this
	}

	getViewVariables(): { title: string; breadcrumbTitle: string; description: string } {
		return {
			title: this.isNew
				? translate('de', 'UI.mmpv.materialProperty.modal.title.new')
				: translate('de', 'UI.mmpv.materialProperty.modal.title.existing', {
						materialProperty: this.materialProperty.name
					}),
			breadcrumbTitle: this.isNew
				? translate('de', 'UI.mmpv.materialProperty.breadcrumb.new')
				: this.materialProperty.name,
			description: this.isNew
				? translate('de', 'UI.mmpv.materialProperty.modal.description.new')
				: translate('de', 'UI.mmpv.materialProperty.modal.description.existing')
		}
	}

	setViewVariables(): void {
		const variables = this.getViewVariables()
		this.title = variables.title
		this.breadcrumbTitle = variables.breadcrumbTitle
		this.description = variables.description
	}

	/**
	 * Generates tooltip information for material property parents if available.
	 *
	 * The function checks for existence of the given material property in the property parents collection (i.e., $propertyParents)
	 * and builds an HTML list of parent properties, which is assigned to `materialPropertyTooltip`. If the given
	 * material property does not exist, `materialPropertyTooltip` is set to an empty string instead.
	 *
	 * @function buildMaterialPropertyParentsInfoTooltip
	 */
	buildMaterialPropertyParentsInfoTooltip(): void {
		const parentIds = this.#propertyParents.get(this.materialProperty.id)
		if (parentIds) {
			const list: string = parentIds
				.filter((parentId: string) => this.#properties.get(parentId) !== undefined)
				.map(
					(parentId: string) =>
						`<li onclick="document.dispatchEvent(new CustomEvent('tooltipRedirect', {detail: '${parentId}'}))" style="cursor: pointer; margin-top: 0.5rem">
                    ${this.#properties.get(parentId)!.name}
                </li>`
				)
				.join('')
			this.materialPropertyTooltip = translate('de', 'UI.mmpv.materialProperty.parents.tt', {
				materialProperty: this.materialProperty.name,
				list: list
			})
		} else {
			this.materialPropertyTooltip = ''
		}
	}
}

export class MaterialPropertyListElementHandler extends ViewHandler {
	propertyElement: MaterialPropertyListElement
	constructor(propertyElement: MaterialPropertyListElement) {
		super(MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT)
		this.propertyElement = propertyElement
		this.setViewVariables()
		return this
	}
	getViewVariables(): {
		title: string
		breadcrumbTitle: string
		description: string
		materialPropertyTooltip: string
	} {
		return {
			title: translate('de', 'UI.mmpv.listElement.modal.title', {
				listElement: this.propertyElement.name
			}),
			breadcrumbTitle: this.propertyElement.name,
			description: translate('de', 'UI.mmpv.listElement.modal.description'),
			materialPropertyTooltip: ''
		}
	}

	setViewVariables(): void {
		const variables = this.getViewVariables()
		this.title = variables.title
		this.breadcrumbTitle = variables.breadcrumbTitle
		this.description = variables.description
		this.materialPropertyTooltip = variables.materialPropertyTooltip
	}

	buildMaterialPropertyParentsInfoTooltip(): void {
		this.materialPropertyTooltip = ''
	}
}
